import React, { useCallback, useContext, useEffect, useState } from "react";
import { FaSyncAlt } from "react-icons/fa";
import { AlertTriangle, Download, FileSpreadsheet, Inbox } from "lucide-react";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import { isRTL } from "../../utils/rtl";
import { toast } from "../../utils/toast";
import CommonButton from "../common/CommonButton";
import CommonTooltip from "../common/CommonTooltip";
import PageSizeSelector from "../common/PageSizeSelector";
import PagerArrowButton from "../common/PagerArrowButton";
import { UploadStatusBadge } from "./inventoryUploadShared";
import {
  fetchAllInventoryUploadRejectedRows,
  fetchInventoryUploadList,
} from "../../service/MeterInventoryManagement";
import {
  downloadRejectedRowsExcel,
  rejectedRowsFileNamePrefix,
} from "../../utils/inventoryRejectedRowsExport";
import {
  InlineSpinner,
  apiErrorMessage,
  firstArray,
  firstNumber,
  unwrap,
  useInventoryScope,
} from "./inventoryFormShared";

const ACTIVE_STATUSES = new Set(["QUEUED", "PENDING", "IN_PROGRESS", "PROCESSING"]);
const ACTIVE_REFRESH_MS = 4000;

/**
 * List of previous sheet uploads (the Excel Upload tab's history view on Add
 * Inventory). `refreshKey` lets the upload form reload the list when a run starts
 * or finishes.
 */
export const InventoryUploadHistory = ({ refreshKey = 0 }) => {
  const { theme, langMode } = useContext(AppContext);
  const isRtl = isRTL(langMode);
  const t = translations[langMode] || translations.en;
  const { organizationId, projectId } = useInventoryScope();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  // Uploads no longer make a separate validation run, so the history lists real
  // imports only.
  const includeValidateOnly = false;
  const [exportingUploadId, setExportingUploadId] = useState(null);

  // `silent` skips the loading overlay for the background refresh below.
  const loadHistory = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const payload = unwrap(
        await fetchInventoryUploadList({
          page,
          size: pageSize,
          projectId,
          includeValidateOnly,
        })
      );
      const items = Array.isArray(payload)
        ? payload
        : firstArray(
            payload?.uploadList,
            payload?.inventoryUploadList,
            payload?.content,
            payload?.items,
            payload?.data
          );
      setRows(items);
      setTotal(
        firstNumber(
          payload?.totalSize,
          payload?.totalElements,
          payload?.totalCount,
          payload?.total,
          items.length
        ) ?? 0
      );
    } catch (error) {
      // A failed background refresh keeps the rows already on screen.
      if (!silent) {
        setRows([]);
        setTotal(0);
        toast.error(apiErrorMessage(error, t.inventoryImportHistoryLoadFailed));
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, pageSize, projectId, includeValidateOnly, t]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory, refreshKey]);

  // Uploads import in the background, so while any listed run is still queued or
  // running the list re-reads itself until they all reach a final status.
  const hasActiveRun = rows.some((row) =>
    ACTIVE_STATUSES.has(String(row.status || "").toUpperCase())
  );
  useEffect(() => {
    if (!hasActiveRun) return undefined;
    const timer = setTimeout(() => loadHistory({ silent: true }), ACTIVE_REFRESH_MS);
    return () => clearTimeout(timer);
  }, [hasActiveRun, rows, loadHistory]);

  // The list payload caps its inline `notUploaded` array (flagging
  // `notUploadedTruncated`), so the export pulls the full reject list from the
  // paged rows endpoint and only falls back to the inline rows when that call
  // comes back empty.
  const handleDownloadNotUploaded = useCallback(
    async (row) => {
      const uploadId = row.id ?? row.uploadId ?? row.inventoryUploadId;
      const inlineRows = firstArray(row.notUploaded, row.errors, row.rowErrors);
      setExportingUploadId(uploadId ?? null);
      try {
        let rejected = inlineRows;
        if (uploadId) {
          const fetched = await fetchAllInventoryUploadRejectedRows(uploadId, {
            organizationId,
            projectId,
          });
          if (fetched.length > 0) rejected = fetched;
        }
        if (!downloadRejectedRowsExcel(rejected, rejectedRowsFileNamePrefix(row))) {
          toast.info(t.inventoryImportNotUploadedExportEmpty);
        }
      } catch (error) {
        // A failed page walk still leaves the inline rows worth exporting.
        if (downloadRejectedRowsExcel(inlineRows, rejectedRowsFileNamePrefix(row))) {
          toast.warn(t.inventoryImportNotUploadedExportPartial);
        } else {
          toast.error(apiErrorMessage(error, t.inventoryImportNotUploadedExportFailed));
        }
      } finally {
        setExportingUploadId(null);
      }
    },
    [organizationId, projectId, t]
  );

  const totalPages = Math.max(Math.ceil(total / pageSize), 1);
  const startIndex = total === 0 ? 0 : page * pageSize + 1;
  const endIndex = total === 0 ? 0 : Math.min(total, (page + 1) * pageSize);

  const formatDateTime = (value) => {
    if (!value) return t.notAvailable;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString(langMode === "ar" ? "ar" : langMode === "ur" ? "ur" : "en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div
      className="flex min-h-[420px] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
      data-testid="inventory-import-history"
    >
      <div className="flex min-h-[56px] shrink-0 flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-2.5">
        <div>
          <p className="text-sm font-semibold text-slate-700">{t.inventoryImportHistoryHeading}</p>
          <p className="text-xs text-slate-400">
            {startIndex}-{endIndex} {t.of} {total}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs font-medium text-slate-500">
          <span>{t.rowsPerPage}</span>
          <PageSizeSelector
            value={pageSize}
            onChange={(value) => {
              setPage(0);
              setPageSize(value);
            }}
            height="32px"
            fontSize="12px"
          />
          <PagerArrowButton
            direction="prev"
            isRtl={isRtl}
            onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
            disabled={page === 0}
          />
          <span className="min-w-[38px] text-center tabular-nums text-slate-600">
            {page + 1} / {totalPages}
          </span>
          <PagerArrowButton
            direction="next"
            isRtl={isRtl}
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages - 1))}
            disabled={page >= totalPages - 1}
          />
          <CommonButton colorIntent="primary"
            onClick={() => loadHistory()}
            variant="primary"
            theme={theme}
            disabled={loading}
            aria-label={t.refresh}
            wrapperClassName="shrink-0"
            className="!h-8 !w-8 sm:!w-auto !p-0 sm:!px-3 !justify-center"
            data-testid="inventory-import-refresh-button"
          >
            <FaSyncAlt size={13} className={loading ? "animate-spin" : "shrink-0"} />
            <span className="hidden whitespace-nowrap sm:inline">{t.refresh}</span>
          </CommonButton>
        </div>
      </div>

      {/* The spinner overlays the centre of the table viewport, so it sits outside the
          scroll container -- inside it, the overlay would scroll away with the rows. */}
      <div className="relative min-h-0 w-full flex-1">
        {loading && (
          <div
            className="absolute inset-0 z-20 flex items-center justify-center bg-white/70"
            data-testid="inventory-import-history-loading"
          >
            <div className="flex flex-col items-center gap-2 text-xs text-slate-500">
              <InlineSpinner size={28} />
              {t.Processing}
            </div>
          </div>
        )}
        <div className="h-full w-full overflow-auto">
          <table className="w-full min-w-[1100px] table-auto text-start text-xs text-slate-600">
            <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 text-[11px] font-semibold uppercase tracking-[0.04em] text-slate-500">
              <tr>
                <th className="px-4 py-3 text-start">{t.inventoryImportHistoryFile}</th>
                <th className="px-4 py-3 text-start">{t.inventoryImportHistoryStore}</th>
                <th className="px-4 py-3 text-start">{t.inventoryItemType}</th>
                <th className="px-4 py-3 text-start">{t.inventoryImportHistoryMode}</th>
                <th className="px-4 py-3 text-start">{t.inventoryImportHistoryStatus}</th>
                <th className="px-4 py-3 text-start">{t.inventoryImportHistoryTotal}</th>
                <th className="px-4 py-3 text-start">{t.inventoryImportCreatedRows}</th>
                <th className="px-4 py-3 text-start">{t.inventoryImportUpdatedRows}</th>
                <th className="px-4 py-3 text-start">{t.inventoryImportSkippedRows}</th>
                <th className="px-4 py-3 text-start">{t.inventoryImportFailedRows}</th>
                <th className="px-4 py-3 text-start">{t.inventoryImportNotUploadedCount}</th>
                <th className="px-4 py-3 text-start">{t.inventoryImportHistoryUploadedBy}</th>
                <th className="px-4 py-3 text-start">{t.inventoryImportHistoryUploadedAt}</th>
                <th className="px-4 py-3 text-start">{t.download}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading ? (
                <tr>
                  <td colSpan={14} className="py-16 align-middle">
                    <div className="flex flex-col items-center justify-center text-center">
                      <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                        <Inbox size={22} />
                      </div>
                      <div className="text-sm font-semibold text-slate-800">
                        {t.inventoryImportHistoryEmpty}
                      </div>
                      <div className="mt-1 max-w-xs text-xs text-slate-400">
                        {t.inventoryImportHistorySubtitle}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => {
                  const uploadId = row.id ?? row.uploadId ?? row.inventoryUploadId;
                  return (
                    <tr
                      key={uploadId ?? index}
                      className="border-b border-slate-100 odd:bg-white even:bg-slate-50/60"
                      data-testid="inventory-import-history-row"
                    >
                      <td className="px-4 py-3 font-medium text-slate-700">
                        <span className="flex flex-wrap items-center gap-1.5">
                          <span className="max-w-[260px] truncate" title={row.fileName || ""}>
                            {row.fileName ?? t.notAvailable}
                          </span>
                          {row.validateOnly && (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">
                              {t.inventoryImportValidationRunBadge}
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3">{row.storeName ?? row.storeId ?? t.notAvailable}</td>
                      <td className="px-4 py-3">{row.itemType ?? t.notAvailable}</td>
                      <td className="px-4 py-3">{row.uploadMode ?? t.notAvailable}</td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span className="flex items-center gap-1.5" title={row.failureReason || ""}>
                          <UploadStatusBadge status={row.status} t={t} />
                          {row.failureReason && (
                            <AlertTriangle size={13} className="shrink-0 text-red-500" />
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 tabular-nums">{row.totalRowCount ?? t.notAvailable}</td>
                      <td className="px-4 py-3 tabular-nums">{row.createdCount ?? t.notAvailable}</td>
                      <td className="px-4 py-3 tabular-nums">{row.updatedCount ?? t.notAvailable}</td>
                      <td className="px-4 py-3 tabular-nums">{row.skippedCount ?? t.notAvailable}</td>
                      <td className="px-4 py-3 tabular-nums">{row.failedCount ?? t.notAvailable}</td>
                      <td className="px-4 py-3 tabular-nums">
                        <span className="flex items-center gap-2">
                          {row.notUploadedCount ?? t.notAvailable}
                          {(row.notUploadedCount ?? 0) > 0 &&
                            (exportingUploadId === uploadId ? (
                              <InlineSpinner size={13} />
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleDownloadNotUploaded(row)}
                                title={t.inventoryImportDownloadNotUploaded}
                                aria-label={t.inventoryImportDownloadNotUploaded}
                                className="text-[#236DB4] transition hover:text-[#1d5a96]"
                                data-testid="inventory-import-download-not-uploaded"
                              >
                                <FileSpreadsheet size={13} />
                              </button>
                            ))}
                          {row.failureLogUrl && (
                            <a
                              href={row.failureLogUrl}
                              download
                              title={t.inventoryImportDownloadFailureLog}
                              className="text-[#236DB4] hover:text-[#1d5a96]"
                            >
                              <Download size={13} />
                            </a>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3">{row.uploadedByName ?? t.notAvailable}</td>
                      <td className="whitespace-nowrap px-4 py-3">{formatDateTime(row.uploadedAt)}</td>
                      <td className="whitespace-nowrap px-4 py-3">
                        {row.fileUrl ? (
                          <CommonTooltip
                            text={t.donwloastheuplodedexcel}
                            position="top"
                            disableDefaultDescription
                            tooltipClassName="w-max max-w-[min(12rem,calc(100vw-1.5rem))] [text-wrap:balance]"
                            fitContent
                            clampToViewport
                            hoverOnly
                          >
                            <a
                              href={row.fileUrl}
                              download
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={t.donwloastheuplodedexcel}
                              data-testid="inventory-import-download-file"
                              className="inline-flex items-center gap-1.5 rounded-lg bg-[#236DB4] px-3 py-1.5 text-xs font-medium text-white transition hover:bg-[#1d5a96]"
                            >
                              <Download size={13} />
                              {t.download}
                            </a>
                          </CommonTooltip>
                        ) : (
                          t.notAvailable
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InventoryUploadHistory;
